import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { IEmailNotification, IInviteContainer, IOrderCompletedNotification } from '../../../Model/Invite/IInvite.js'
import { removeRequest } from 'Flux/FluxUtils.ts'

export interface IInvitationState {
  invites: IInviteContainer | 'LOADING'
  currentInvite?: IEmailNotification | 'LOADING' | 'ERROR'
  openRequests: string[]
  /** Received invites still to be accepted or declined. Kept through a refetch, so the sidebar badge never blinks. */
  pendingReceived?: number
}

export const countPendingReceived = (invites: IInviteContainer): number =>
  (invites.received?.labInvites?.length ?? 0) + (invites.received?.projectInvites?.length ?? 0)

const initialState: IInvitationState = {
  invites: 'LOADING',
  currentInvite: undefined,
  openRequests: []
}

const reducers = {
  // Actions moved from ProjectSlice
  getAllInvitesRequest: (state: Draft<IInvitationState>, action: PayloadAction) => {
    state.openRequests.push(action.type)
    state.invites = 'LOADING'
  },
  getAllInvitesRequestSuccess: (state: Draft<IInvitationState>, action: PayloadAction<IInviteContainer>) => {
    state.invites = action.payload
    state.pendingReceived = countPendingReceived(action.payload)
    state.openRequests = removeRequest(state, action)
  },
  getAllInvitesRequestFailure: (state: Draft<IInvitationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  saveNewOrderCompletedNotificationRequest: (
    state: Draft<IInvitationState>,
    action: PayloadAction<Omit<IOrderCompletedNotification, 'id' | 'orderName'>>
  ) => {
    state.openRequests.push(action.type)
  },
  saveNewOrderCompletedNotificationRequestSuccess: (state: Draft<IInvitationState>, action: PayloadAction<IOrderCompletedNotification>) => {
    state.openRequests = removeRequest(state, action)
    if (state.invites != 'LOADING')
      state.invites.send.orderCompletedNotifications = [...(state.invites.send.orderCompletedNotifications ?? []), action.payload]
  },
  saveNewOrderCompletedNotificationRequestFailure: (
    state: Draft<IInvitationState>,
    action: PayloadAction<{
      error: Error
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
  },
  updateOrderCompletedNotificationRequest: (
    state: Draft<IInvitationState>,
    action: PayloadAction<Omit<IOrderCompletedNotification, 'orderName'>>
  ) => {
    state.openRequests.push(action.type)
  },
  updateOrderCompletedNotificationRequestSuccess: (state: Draft<IInvitationState>, action: PayloadAction<IOrderCompletedNotification>) => {
    state.openRequests = removeRequest(state, action)
    if (state.invites != 'LOADING' && !!state.invites.send.orderCompletedNotifications)
      state.invites.send.orderCompletedNotifications = [
        ...state.invites.send.orderCompletedNotifications.filter((notification) => notification.id !== action.payload.id),
        action.payload
      ]
  },
  updateOrderCompletedNotificationRequestFailure: (
    state: Draft<IInvitationState>,
    action: PayloadAction<{
      error: Error
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
  },
  getInviteByIdRequest: (state: Draft<IInvitationState>, action: PayloadAction<{ inviteId: number }>) => {
    state.currentInvite = 'LOADING'
    state.openRequests.push(action.type)
  },
  getInviteByIdRequestSuccess: (state: Draft<IInvitationState>, action: PayloadAction<IEmailNotification>) => {
    state.currentInvite = action.payload
    state.openRequests = removeRequest(state, action)
  },
  getInviteByIdRequestFailure: (state: Draft<IInvitationState>, action: PayloadAction<{ error: Error }>) => {
    state.currentInvite = 'ERROR'
    state.openRequests = removeRequest(state, action)
  },
  deleteInviteRequest: (state: Draft<IInvitationState>, action: PayloadAction<{ inviteId: number }>) => {
    state.openRequests.push(action.type)
  },
  deleteInviteRequestSuccess: (state: Draft<IInvitationState>, action: PayloadAction<{ inviteId: number }>) => {
    if (state.invites != 'LOADING') {
      state.invites = {
        received: {
          labInvites: state.invites?.received?.labInvites?.filter((x) => x.id != action.payload.inviteId),
          orderCompletedNotifications: state.invites?.received?.orderCompletedNotifications?.filter((x) => x.id != action.payload.inviteId),
          projectInvites: state.invites?.received?.projectInvites?.filter((x) => x.id != action.payload.inviteId)
        },
        send: {
          labInvites: state.invites?.send?.labInvites?.filter((x) => x.id != action.payload.inviteId),
          orderCompletedNotifications: state.invites?.send?.orderCompletedNotifications?.filter((x) => x.id != action.payload.inviteId),
          projectInvites: state.invites?.send?.projectInvites?.filter((x) => x.id != action.payload.inviteId)
        }
      }
      state.currentInvite = 'ERROR'
      state.pendingReceived = countPendingReceived(state.invites)
    }
    state.openRequests = removeRequest(state, action)
  },
  deleteInviteRequestFailure: (state: Draft<IInvitationState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  // Actions moved from AuthenticationSlice
  acceptInvitationRequest: (
    state: Draft<IInvitationState>,
    action: PayloadAction<{
      invitationId: string
      password: string
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  acceptInvitationSuccess: (state: Draft<IInvitationState>) => {
    state.openRequests = state.openRequests.filter((request) => request !== invitationSlice.actions.acceptInvitationRequest.type)
  },
  acceptInvitationFailure: (state: Draft<IInvitationState>) => {
    state.openRequests = state.openRequests.filter((request) => request !== invitationSlice.actions.acceptInvitationRequest.type)
  },

  reset: () => {
    return initialState
  }
}

export const invitationSlice = createSlice({
  name: 'invitation',
  initialState,
  reducers
})
